import videojs from 'video.js';
import MessageList from './message-list';
import { throttle } from '../../js/helpers/throttle-debounce';
import utils from './utils';
import commentReplyTemplate from '../templates/comment-reply.html';
import iconDeleteTemplate from '../templates/delete.svg.html';

const Component = videojs.getComponent('Component');

const defaultOptions = {
  messageCounter: 0,
  messages: [],
  avatar: '',
  showMessagesByTime: true
};

export default class ChatMessanger extends Component {
  constructor(player, options) {
    super(player, options);
    this.player = player;
    this.videoEl = player.el().querySelector('video');
    this.options = videojs.mergeOptions(defaultOptions, options);
    this.commentTextEl = this.el().querySelector('.vjs-chat-form-body__textarea');
    this.sendCommentBtnEl = this.el().querySelector('.vjs-chat-form-body__button-send');
    this.messageContainer = this.el().querySelector('.vjs-chat-content');
    this.chatMessangerWrapper = this.el().querySelector('.vjs-chat-messanger-wrapper');
    this.pinnedMessageContainer = this.el().querySelector('.vjs-chat-pinned-message');
    this.formEl = this.el().querySelector('.vjs-chat-form');
    this.loginBtnEl = this.el().querySelector('.vjs-chat-menu__login');

    this.timeoutIdHide = null;

    this.replyMessageId = null;
    this.replyContainerEl = null;
    this.prevUpdateTime = null;
    this.isManualChatMode = false;

    this.messageList = new MessageList({
      container: this.messageContainer,
      pinnedMessageContainer: this.pinnedMessageContainer,
      hideMessages: true,
      chatApi: options.chatApi,
      getGeneratedAvatarForUser: options.getGeneratedAvatarForUser
    });

    this.lastSentMessage = {};
    this.isBlockSendMessage = false;

    this.authEnabled = false;
    this.autoScrollOnUpdate = true;
    this.live_shift = this.options.live_shift;
    this.showMessagesByTime = this.options.showMessagesByTime;
    this.chatAllowMessagesForVOD = this.options.chatAllowMessagesForVOD;

    this.setListeners();
    this.setChatContentBlur(this.messageContainer);

    const avatarEl = this.el().querySelector('.vjs-chat-form-body__avatar');
    avatarEl.src = this.options.avatar;
  }

  contentEl() {
    return this.el().querySelector('.vjs-chat-form-body');
  }

  get chatApi() {
    return this.options.chatApi;
  }

  get authEnabled() {
    return this._authEnabled;
  }

  get iOSUser() {
    if (this._iOSUser === undefined) {
      this._iOSUser = !this.player.hls_ && utils.iOSUser();
    }

    return this._iOSUser;
  }

  /**
   * @description Вычисляет задержку трансляции
   */
  get liveLag() {
    var mediaElement = this.player.tech().el();
    var bufferedRanges = mediaElement.buffered;
    if (bufferedRanges.length > 0) {
      var endDelay = bufferedRanges.end(bufferedRanges.length - 1);
      return (endDelay - mediaElement.currentTime) * 1000;
    }
    return 0;
  }

  set authEnabled(value) {
    this._authEnabled = value;
    // В зависимости от доступности авторизации управляем видимостью элементов авторизации
    if (this._authEnabled) {
      this.commentTextEl.classList.remove('vjs-hidden');
      this.sendCommentBtnEl.classList.remove('vjs-hidden');
      this.el().querySelector('.vjs-chat-form-body__avatar-container').classList.remove('vjs-hidden');
      this.el().querySelector('.vjs-chat-form-body__textarea-locker .vjs-chat-auth-invite').classList.remove('vjs-hidden');
      this.el().querySelector('.vjs-chat-form-body__textarea-locker .vjs-chat-auth-disabled').classList.add('vjs-hidden');
      this.el().querySelector('.vjs-chat-header__btn').classList.remove('vjs-hidden');
    } else {
      this.commentTextEl.classList.add('vjs-hidden');
      this.sendCommentBtnEl.classList.add('vjs-hidden');
      this.el().querySelector('.vjs-chat-form-body__avatar-container').classList.add('vjs-hidden');
      this.el().querySelector('.vjs-chat-form-body__textarea-locker .vjs-chat-auth-invite').classList.add('vjs-hidden');
      this.el().querySelector('.vjs-chat-form-body__textarea-locker .vjs-chat-auth-disabled').classList.remove('vjs-hidden');
      this.el().querySelector('.vjs-chat-header__btn').classList.add('vjs-hidden');
    }
  }

  createEl() {
    const el = videojs.createEl('div', {
      className: 'vjs-chat-messanger vjs-hidden',
    });
    el.dataset.auth = false;

    el.insertAdjacentHTML('afterbegin', this.getTemplate());

    return el;
  }

  setHeight(height) {
    this.el().style.height = height;
  }

  resetHeight() {
    this.el().style.height = '';
  }

  handleResize() {
    function hasSizeWClassName(classList) {
      let isSizeWClassName = false;
      for (const value of classList.values()) {
        isSizeWClassName = value.indexOf('vjs-size-w') === 0;
        if (isSizeWClassName) break;
      }
      return isSizeWClassName;
    }

    if (this.player.el_.dataset.viewMode === 'mobile' && hasSizeWClassName(this.player.el_.classList)) {
      const playerHeight = this.player.el_.clientHeight;
      if (playerHeight <= 300) {
        this.el().style.height = `calc(${playerHeight}px + var(--vjs-chat-messanger-textarea-scroll-height))`;
      } else {
        this.el().style.height = `calc(${Math.max(300, playerHeight / 3)}px + var(--vjs-chat-messanger-textarea-scroll-height))`;
      }
    } else if (this.player.el().dataset.viewMode !== 'tablet') {
      this.el().style.height = '';
    }

    this.scrollMessageContainter();
    this.resizeTextarea(this.commentTextEl);

    this.shouldGap()
      ? this.setGap()
      : this.removeGap();
  }

  resizeTextarea(el) {
    const defaultScrollHeight = 2;
    const scrollHeight = parseInt(this.el().style.getPropertyValue('--vjs-chat-messanger-textarea-scroll-height') || `${defaultScrollHeight}px`, 10);
    this.el().style.setProperty('--vjs-chat-messanger-textarea-scroll-height', `${defaultScrollHeight}px`);
    el.classList.add('vjs-chat-form-body_overflow-hidden');
    const scrollHeightDiff = el.scrollHeight - el.clientHeight;
    if (scrollHeightDiff >= 0) {
      const isMobile = this.player.el_.dataset.viewMode === 'mobile';
      const incrementToHideScrollbar = defaultScrollHeight;
      const isScrollbar = isMobile
        ? this.currentHeight() + scrollHeightDiff > this.player.currentHeight()
        : scrollHeightDiff > 110;
      const newScrollHeight = `${isScrollbar ? scrollHeight : scrollHeightDiff + incrementToHideScrollbar}px`;
      if (isScrollbar) {
        el.classList.remove('vjs-chat-form-body_overflow-hidden');
      }
      this.el().style.setProperty('--vjs-chat-messanger-textarea-scroll-height', newScrollHeight);
      this.trigger({type: 'ChatMessanger/resizeTextarea', payload: newScrollHeight});
    } else {
      this.trigger({type: 'ChatMessanger/resizeTextarea', payload: `${defaultScrollHeight}px`});
    }
  }

  setChatContentBlur({scrollTop, clientHeight, scrollHeight}) {
    const isUpper = scrollTop === 0;
    const isLower = (scrollTop + clientHeight) >= scrollHeight;
    const isWithoutScroll = scrollHeight === clientHeight;
    if (isWithoutScroll) {
      this.chatMessangerWrapper.classList.add('vjs-chat-content_without-opacity');
      this.chatMessangerWrapper.classList.remove('vjs-chat-content_opacity-top');
      this.chatMessangerWrapper.classList.remove('vjs-chat-content_opacity-bottom');
    } else if (isUpper) {
      this.chatMessangerWrapper.classList.remove('vjs-chat-content_without-opacity');
      this.chatMessangerWrapper.classList.remove('vjs-chat-content_opacity-top');
      this.chatMessangerWrapper.classList.add('vjs-chat-content_opacity-bottom');
    } else if (isLower) {
      this.chatMessangerWrapper.classList.remove('vjs-chat-content_without-opacity');
      this.chatMessangerWrapper.classList.add('vjs-chat-content_opacity-top');
      this.chatMessangerWrapper.classList.remove('vjs-chat-content_opacity-bottom');
    } else {
      this.chatMessangerWrapper.classList.remove('vjs-chat-content_without-opacity');
      this.chatMessangerWrapper.classList.remove('vjs-chat-content_opacity-top');
      this.chatMessangerWrapper.classList.remove('vjs-chat-content_opacity-bottom');
    }
  }

  setGap() {
    if (this.player.el().classList.contains('vjs-stream-shopping_visible')) {
      this.el().querySelector('.vjs-chat-form').classList.add('vjs-chat-form_indent-top');
    }
    this.setChatContentBlur(this.messageContainer);
  }

  removeGap() {
    this.el().querySelector('.vjs-chat-form').classList.remove('vjs-chat-form_indent-top');
    this.setChatContentBlur(this.messageContainer);
  }

  shouldGap() {
    return [ ...this.player.el_.classList.values() ]
      .some((className) => className.startsWith('vjs-size-w'));
  }

  enableForm() {
    this.commentTextEl.disabled = false;
    this.sendCommentBtnEl.disabled = false;
  }

  disableForm() {
    this.commentTextEl.disabled = true;
    this.sendCommentBtnEl.disabled = true;
  }

  ownMessage(message) {
    return message.text === this.lastSentMessage.text &&
             message.author.name === this.chatApi.userData.name;
  }

  isCurrentUserId(userId) {
    if (!this.chatApi.userData) {
      return false;
    }
    return userId === this.chatApi.userData.userID;
  }

  handleIsPinnedMessageBlocked({ messageIds, isAuthorBlocked }) {
    const isPinnedMessageBlocked = this.messageList.hasOriginalPinnedMessage(messageIds);
    if (!isPinnedMessageBlocked) return;
    this.messageList.togglePinnedMessageVisibility(!isAuthorBlocked);
  }

  handleBlockCurrentUser(isPermanentBlock) {
    const blockedTextEl = this.el().querySelector('.vjs-chat-form-body__blocked-text');
    const blockedText = isPermanentBlock ? 'Отправка сообщений ограничена' : 'Отправка сообщений ограничена на 3 минуты';
    blockedTextEl.innerText = blockedText;
    this.el().dataset.auth = 'blocked';
  }

  setListeners() {
    this.player.on('ready', () => {
      const buttonToggle = this.player.getChild('controlBar').getChild('ButtonToggleStreamShopping');
      if (buttonToggle) {
        buttonToggle.on('StreamShopping/visible', () => {
          this.shouldGap()
            ? this.setGap()
            : this.removeGap();
        });
        buttonToggle.on('StreamShopping/hidden', this.removeGap.bind(this));
      }
    })

    this.messageContainer.addEventListener('scroll', (event) => {
      this.setChatContentBlur(event.target);
      // Осуществляем прокрутку сообщений в чате при отображении их по таймлайну, если отображается
      // самое последнее сообщение
      this.autoScrollOnUpdate = this.messageContainerScrolledToBottom();
    });

    this.commentTextEl.addEventListener('input', (event) => {
      this.resizeTextarea(event.target);
    });

    this.player.one('playing', () => {
      this.toggleVisibility();
      this.handleResize();
      this.scrollMessageContainter();
      window.addEventListener('resize', throttle(500, false, this.handleResize.bind(this)));
    });

    this.player.on('ended', () => {
      this.messageList.showAll();
    })

    this.chatApi.on('newMessage', (event) => {
      let message = event.data;
      let isLive = this.checkLive();
      // Для live контента с включенной настройкой chat_show_messages_by_time 
      // пересчитаем время отправки сообщения т.к. некорректно указывать Date.now()
      // потому что пользователю отображается контент с некоторой задержкой
      if (this.showMessagesByTime && isLive && !this.player.ended()) {
        message.time = Date.now() - this.live_shift * 1000;
        // Сразу скрываем сообщение при включенной настройке chat_show_messages_by_time
        // сообщение отобразится в соотвтествующий момент видео с помощью функции updateChat
        this.messageList.addMessage(message, 'hide');
      } else {
        this.messageList.addMessage(message);
      }
      if (!this.showMessagesByTime) {
        this.scrollMessageContainter();
      }
      if (this.ownMessage(message)) {
        this.enableForm();
        this.commentTextEl.value = '';
        this.resizeTextarea(this.commentTextEl);
      }
    });

    this.chatApi.on('addMessageError', () => {
      this.enableForm();
    });

    this.chatApi.on('pinnedMessage', (event) => {
      this.handlePinnedMessage(event.data);
    });

    this.chatApi.on('pinMessage', (event) => {
      this.handlePinnedMessage(event.data);
    });

    this.chatApi.on("unpinMessage", () => {
      this.messageList.cleanPinnedMessage();
    });

    this.chatApi.on('blockAuthor', ({ data }) => {
      this.handleIsPinnedMessageBlocked({
        messageIds: data.messageIds,
        isAuthorBlocked: true
      });

      data.messageIds.forEach((messageId) => {
        this.messageList.updateMessageDataPublished(messageId, false);
        this.messageList.toggleMessageVisibility(messageId, false);
        this.messageList.toggleMessageReplyVisibility(messageId, false);
      });

      if (!this.isCurrentUserId(data.author.id)) return;
      this.handleBlockCurrentUser(data.author.permanentBlock);
    });

    this.chatApi.on('unblockedAuthors', ({ data }) => {
      const isCurrentUserUnblocked = data.authorIds.some((authorId) => this.isCurrentUserId(authorId));
      if (!isCurrentUserUnblocked) return;
      this.el().dataset.auth = true;
    });

    this.chatApi.on('authorizeAnswer', ({ data }) => {
      if (!data.blocked) return;
      const isAuthorBlocked = Boolean(data.blocked.blockedTill);
      const isAuthorPermanentBlocked = data.blocked.permanentBlock;

      if (isAuthorBlocked) {
        this.el().dataset.auth = 'blocked';
        this.handleBlockCurrentUser(isAuthorPermanentBlocked);
      } else {
        this.enableForm();
      }
    });

    // Событие авторизации пользователя
    this.chatApi.on('chatAuthorized', (event) => {
      const textareaLocker = this.el().querySelector('.vjs-chat-form-body__textarea-locker');
      if (textareaLocker && this.commentTextEl) {
        this.el().dataset.auth = true;
      }

      const formAvatarEl = this.el().querySelector('.vjs-chat-form-body__avatar');
      formAvatarEl.src = this.chatApi.userData.avatarUrl;
      formAvatarEl.onerror = event => {
        event.target.src = this.options_.getGeneratedAvatarForUser(
          this.chatApi.userData.name
        );
      };
    });

    // Событие деавторизации пользователя
    this.chatApi.on('deauthorized', (event) => {
      const textareaLocker = this.el().querySelector('.vjs-chat-form-body__textarea-locker');
      if (textareaLocker && this.commentTextEl) {
        this.el().dataset.auth = false;
      }

      // Отображаем кнопку "Авторизуйтесь"
      this.el()
        .querySelector('.vjs-chat-menu__login')
        .classList
        .remove('vjs-hidden');

      // Очищаем ответ на сообщение
      this.clearReply();
    });

    this.chatApi.one('chatAuthorized', () => {
      this.el()
        .querySelector('.vjs-chat-menu__login')
        .classList
        .add('vjs-hidden');

      this.loginBtnEl.classList.add('vjs-hidden');
    });

    this.chatApi.on('changeMessage', (event) => {

      // Если по запросу сообщения не оказалось в списке, то рендерим его заново.
      // Рендерим на основе ответа сервиса чата на published сообщение (ref #152203).
      if (!this.messageContainer.querySelector(`#comment-${event.data.messageId}`)) {
        this.messageList.renderMessage(event.data.message)
      }
      this.messageList.updateMessageDataPublished(event.data.messageId, event.data.published);
      this.messageList.toggleMessageVisibility(event.data.messageId, event.data.published);
      this.messageList.toggleMessageReplyVisibility(event.data.messageId, event.data.published);

      const isEventMessagePinned = this.messageList.isOriginalPinnedMessageId(event.data.messageId);
      if (isEventMessagePinned) {
        this.messageList.togglePinnedMessageVisibility(event.data.published);
      }
    });

    this.chatApi.on('connected', () => {
      this.loadHistory();
    });

    this.chatApi.on('reconnected', () => {
      if (this.isBlockSendMessage) {
        this.isBlockSendMessage = false;
        this.sendMessage();
      }
    });

    this.chatApi.on('history', (event) => {
      // Историю сообщений необходимо скроллить, если пользователь уже находится в ее конце. Однако,
      // при первом добавлении сообщений в историю элемент не скроллится самостоятельно до конца -
      // буквально не хватает пару пикселей. Поэтому считаем, что если история сообщений была пуста,
      // то есть необходимость осуществить скролл.
      let needScroll = this.needScrollMessageContainter() || this.messageList.isEmpty;
      this.messageList.clearMessages();
      this.messageList.addMessages(event.data.messages);
      if (needScroll) {
        this.scrollMessageContainter();
      }
      const publishedMessageIds = event.data.messages
        .filter(({ published }) => published)
        .map(({ id }) => id);
      const hasPinnedMessage = this.messageList.hasOriginalPinnedMessage(publishedMessageIds);
      if (hasPinnedMessage) {
        this.messageList.togglePinnedMessageVisibility(true);
      }
    });

    this.el()
      .querySelector('.vjs-chat-menu__login')
      .addEventListener('click', (event) => {
        event.preventDefault();
      });

    // * Скролл к исходному сообщению при клике на закреп.
    this.pinnedMessageContainer.addEventListener('click', (e) => {
      const pinnedMessage = e.target.closest('.vjs-chat-pinned-message');
      if (pinnedMessage) {
        this.scrollToMessage(pinnedMessage.dataset.originalMessageId);
      }
    });

    // * Скролл к исходному сообщению при клике на блок ответа в сообщении
    this.messageContainer.addEventListener("click", (e) => {
      const replyEl = e.target.closest(".vjs-chat-comment__reply");
      if (replyEl) {
        this.scrollToMessage(replyEl.dataset.originalMessageId);
      }
    });

    // * Обрабатываем ответ на выбранное сообщение
    this.messageContainer.addEventListener("click", (e) => {
      const isParentReplyButton = Boolean(e.target.closest('.vjs-chat-comment__reply-button'));
      const isTargetReplyButton = e.target.classList.contains('vjs-chat-comment__reply-button');
      if (!isParentReplyButton && !isTargetReplyButton) return;

      const messageEl = e.target.closest('.vjs-chat-comment');
      if (!messageEl) return;

      const messageId = messageEl.getAttribute('id').replace('comment-', '');
      const message = this.messageList.getMessageById(Number(messageId));
      if (!message) return;

      this.reply(message);
    });

    // Выполняем проверку возможности использования чата
    this.player.one('play', () => {
      const intervalId = this.setInterval(() => this.checkChat(), 100);
      // При переходе в режим read only перестаем выполнять проверки доступности чата
      this.on('chatStateInitialized', () => {
        this.clearInterval(intervalId);
        if (this.showMessagesByTime) {
          this.player.on('seeked', () => {
            this.updateChat('seeked');
          });
          this.player.on('timeupdate', () => {
            this.updateChat('timeupdate');
          });
        }
      });
    });

    // Обработчики скрола колесиком мыши и скрола на сенсорных устройствах
    this.chatMessangerWrapper.addEventListener('wheel', () => this.changeChatMode());
    this.chatMessangerWrapper.addEventListener('touchmove', () => this.changeChatMode());

    this.loginBtnEl
      .addEventListener('click', () => {
        if (!this.authEnabled) return;

        this.chatApi.toggleAuthPopup('auth');
        this.chatApi.toggleAuthForm('enable');
      });

      // Отправляет сообщение из формы
      const handleSendForNotAuthUser = () => {
        this.chatApi.toggleAuthPopup('send');
        this.chatApi.toggleAuthForm('enable');
        this.chatApi.one('authorized', () => {
          this.sendMessage();
        });
      };

      this.el()
        .querySelector('.vjs-chat-form-body__textarea')
        .addEventListener('keypress', (event) => {
          if (event.key === 'Enter') {
            if (this.chatApi.userAuthorized) {
              event.preventDefault();
              this.sendMessage();
            } else {
              handleSendForNotAuthUser();
            }
          }
        });

      this.sendCommentBtnEl
        .addEventListener('click', () => {
          if (this.chatApi.userAuthorized) {
            return this.sendMessage();
          }
          handleSendForNotAuthUser();
        });

      this.el()
        .querySelector('.vjs-chat-header__btn')
        .addEventListener('click', () => {
          if (!this.chatApi.userAuthorized && this.authEnabled) {
            const chatPopupComponent = this.player.getChild('ChatPopup');
            if (chatPopupComponent) {
              chatPopupComponent.toggleVisibility();
            }
          }
        });

    this.messageContainer.addEventListener('click', (event) => {
      let image = event.target.closest('.vjs-chat-comment__image');
      if (!image) return;
      this.options.onShowImage({
        imageSrc: image.src,
        width: image.naturalWidth,
        height: image.naturalHeight
      });
    });
  }

  changeChatMode() {
    if (this.timerId) {
      clearTimeout(this.timerId);
    }
    this.isManualChatMode = true;
    this.timerId = setTimeout(() => {
      this.isManualChatMode = false;
    }, 5000);
  }

  loadHistory() {
    if (!this.options.historyLength || this.options.historyLength < 0) {
      return;
    }
    this.chatApi.loadHistory(this.options.historyLength);
  }

  sendMessage() {
    const attachedFilesComponent = this.getChild('ChatMessangerAttachedFiles');
    const attachedFiles = attachedFilesComponent && attachedFilesComponent.getFiles() || [];

    if (!this.commentTextEl.value.replaceAll(/\s/g, '').length && attachedFiles.length === 0) {
      return;
    }

    if (this.isBlockSendMessage) {
      return;
    }

    this.isBlockSendMessage = true;

    const attachedFileNames = [];

    let sendIndex = 0;
    const sendFiles = (file, onSuccessCallback) => {
      const fileReader = new FileReader();
      fileReader.readAsArrayBuffer(file);
      fileReader.onload = () => {
        const blob = new Blob([
          file.name,
          String.fromCharCode(0x00),
          fileReader.result
        ]);
        this.chatApi.sendBlob(blob);
      };
      this.chatApi.one('imageAdded', (event) => {
        attachedFileNames.push(event.filename);
        if (attachedFiles[sendIndex]) {
          sendFiles(attachedFiles[sendIndex++], onSuccessCallback);
        } else {
          onSuccessCallback();
        }
      });
    };

    this.disableForm();

    const apiSendMessage = () => {
      this.lastSentMessage = { text: this.commentTextEl.value };
      const messageData = {
        messageText: this.commentTextEl.value,
        replyMessageId: this.replyMessageId,
        attachedFileNames: attachedFiles.length ? attachedFileNames : []
      };
      const success = this.chatApi.sendMessage(messageData);
      if (success) {
        this.isBlockSendMessage = false;
        this.commentTextEl.value = '';
        this.resizeTextarea(this.commentTextEl);
        attachedFilesComponent && attachedFilesComponent.clear();
        this.clearReply();
      }
    };

    if (attachedFiles.length) {
      sendFiles(attachedFiles[sendIndex++], apiSendMessage);
    } else {
      apiSendMessage();
    }
  }

  toggleVisibility() {
    return new Promise((resolve) => {
      clearTimeout(this.timeoutIdHide);

      const hasHiddenClass = this.hasClass('vjs-hidden');
      const isDesktop = this.player.el().dataset.viewMode === 'desktop';
      const isStreamShoppingVisible = this.player.hasClass('vjs-stream-shopping_visible');

      /* Задержка должна быть только если мы собираемся скрывать чат (по окончанию анимации "уезда")
        Также не нужно делать задержку если отображается плагин streamShopping, поскольку в таком случае анимации не будет
      */
      const delayBeforeHiding = (!hasHiddenClass && !isStreamShoppingVisible && isDesktop) ? 300 : 0;

      if (hasHiddenClass) {
        this.show();
        this.scrollMessageContainter();
        this.player.addClass('vjs-chat_visible');
        resolve('shown');
      } else {
        this.player.removeClass('vjs-chat_visible');
        this.timeoutIdHide = setTimeout(() => {
          this.hide();
          resolve('hidden');
        }, delayBeforeHiding);
      }

      // setTimeout т.к. this.handleResize должна быть вызвана после переключения видимости
      setTimeout(() => {
        this.handleResize();
        const streamShoppingSlider = this.player.getChild('StreamShoppingSlider');
        if (streamShoppingSlider) {
          streamShoppingSlider.updateWidth();
        }
      }, delayBeforeHiding);
    });
  }

  checkVisibility() {
    return !this.hasClass('vjs-hidden');
  }

  messageContainerScrolledToBottom() {
    return Math.floor(this.messageContainer.scrollHeight - this.messageContainer.scrollTop) === this.messageContainer.clientHeight;
  }

  needScrollMessageContainter() {
    return this.messageContainerScrolledToBottom();
  }

  scrollMessageContainter() {
    this.messageContainer.scrollTo(0, this.messageContainer.scrollHeight);
  }

  scrollToMessage(messageId) {
    const containerScrollOffset = 50;
    const message = this.messageContainer.querySelector(`#${messageId}`);

    if(!message.classList.contains("vjs-chat-comment_highlighted")) {
      message.classList.add("vjs-chat-comment_highlighted");

      setTimeout(() => {
        message.classList.remove("vjs-chat-comment_highlighted");
      }, 1000);
    };

    this.messageContainer.scrollTop = message.offsetTop - containerScrollOffset;
  }

  scrollMessageByTime(time) {
    if (isNaN(time)) return;

    const containerScrollOffset = 300;
    const message = this.messageContainer?.querySelector(`#comment-${this.messageList.messageIdByTime(time)}`);
    if (message) {
      this.messageContainer.scrollTop = message.offsetTop - containerScrollOffset;
    }
  }

  renderMessageCounter(num) {
    const counter = this.el().querySelector('.vjs-chat-menu__counter');
    counter.innerText = num;
  }

  reply(message) {
    this.clearReply();
    this.renderReply(message);
  }

  renderReply(message) {
    const deleteButton = videojs.dom.createEl('button', {
      className: 'vjs-chat-form-reply__delete',
      innerHTML: iconDeleteTemplate({ width: 7, height: 7 })
    });
    deleteButton.addEventListener('click', () => {
      this.clearReply();
    });

    const replyContainerEl = videojs.dom.createEl(
      'div',
      {
        className: 'vjs-chat-form-reply',
        innerHTML: commentReplyTemplate({ message })
      },
      {},
      [ deleteButton ]
    );

    this.formEl.insertBefore(replyContainerEl, this.contentEl());

    this.replyMessageId = message.id;
    this.replyContainerEl = replyContainerEl;

    const streamShoppingSlider = this.player.getChild('StreamShoppingSlider');
    if (streamShoppingSlider) {
      streamShoppingSlider.updateBottomPosition();
    }
  }

  clearReply() {
    if (this.replyContainerEl) {
      this.formEl.removeChild(this.replyContainerEl);
    }

    this.replyMessageId = null;
    this.replyContainerEl = null;

    const streamShoppingSlider = this.player.getChild('StreamShoppingSlider');
    if (streamShoppingSlider) {
      streamShoppingSlider.updateBottomPosition();
    }
  }

  handlePinnedMessage(message) {
    const originalMessage = this.messageList.getMessageById(message.id);
    if (originalMessage) {
      this.pinnedMessageContainer.classList.remove('vjs-hidden');
    }

    this.pinnedMessageContainer.dataset.originalMessageId = `comment-${message.id}`;
    this.messageList.renderPinnedMessage(message);
  }

  get hlsLive () {
    let hls, hlsLevel;
    if (this.player.hls_) {
      hls = this.player.hls_;
      hlsLevel = hls && hls.levels && hls.levels[hls.currentLevel];
      // Если нет данных плейлиста - ничего не делаем
      if (!hlsLevel || !hlsLevel.details) return;
      return hlsLevel && hlsLevel.details && hlsLevel.details.live;
    }
  }

  checkLive() {
    let isLive = false;

    // Для iPad оказаля доступен hls, а duration !== Infinity для Live
    if (this.iOSUser) {
      if (isNaN(this.player.duration())) return;
      if (this.player.hls_) {
        isLive = this.hlsLive;
      } else {
        isLive = this.player.duration() === Infinity;
      }
    } else {
      isLive = this.hlsLive;
    }
    return isLive;
  }

  // Проверка возможности писать в чат
  checkChat() {
    let isLive = this.checkLive();
    if (isLive === undefined) return;

    // Если видео воспроизводится в режиме VOD
    if (!isLive && !this.chatAllowMessagesForVOD) {
      // Переходим в режим чтения
      this.enterReadOnlyMode();
      return this.trigger('chatStateInitialized');
    }

    // Если сейчас трансляция в live

    // Разрешаем авторизацию в чате
    this.authEnabled = true;
    // Отображаем все поступающие сообщения
    this.messageList.hideMessages = false;
    // Отображаем все сообщения, которые были подгружены ранее
    this.messageList.showAll();

    if (this.iOSUser) {
      this.player.on('durationchange', () => {
        if (!isLive && !this.chatAllowMessagesForVOD) {
          this.enterReadOnlyMode();
        }
      });
    } else  {
      // При каждом обновлении плейлиста
      this.player.hls_.on('hlsLevelLoaded', (_event, data) => {
        // Проверяем, не закончилась ли трансляция
        if ((data.details && !data.details.live) && !this.chatAllowMessagesForVOD) {
          // Если трансляция закончилась, то переходим в режим чтения сообщений в чате
          this.enterReadOnlyMode();
        }
      });
    }

    this.trigger('chatStateInitialized');
  }

  updateChat(event) {
    const updateMinSeconds = 1;
    let currentTime = NaN;
    let isLive = this.checkLive();

    // Определяем текущее время в плеере
    if (this.iOSUser) {
      // Для iOS нужно использовать getStartDate
      // (https://developer.apple.com/documentation/webkitjs/htmlmediaelement/1634352-getstartdate)
      // как альтернативу programDateTime
      currentTime = Math.ceil(this.videoEl.getStartDate() * 1 + this.player.currentTime() * 1000);
    } else {
      const hls = this.player.hls_;
      const hlsLevel = hls && hls.levels && hls.levels[hls.currentLevel];
      if (isLive) {
        currentTime = Date.now() - this.liveLag;
      } else {
        currentTime = Math.ceil(hlsLevel && hlsLevel.details.fragments[0].programDateTime + this.player.currentTime() * 1000);
      }
    }

    if (isNaN(currentTime) || !currentTime) {
      return;
    }

    let diffInSec = 0;
    if (this.prevUpdateTime) {
      // Вычисляем количество времени проигранного контента
      diffInSec = Math.abs(Math.floor((currentTime - this.prevUpdateTime) / 1000));
      // Если было проиграно менее 1 секунды, то смысла осуществлять попытку обновления сообщений
      // в чате нет: прошло слишком мало времени
      if (diffInSec < updateMinSeconds) {
        return;
      }
    }
    if (isLive) {
      this.prevUpdateTime = currentTime;
      this.messageList.showForTime(currentTime);

      const seeked = event === 'seeked' || diffInSec > updateMinSeconds;
      // Осуществляем скрол по сообщениям в двух случаях:
      //  1. При перемещении по таймлайну
      //  2. Если пользователь и так видит самое последнее сообщение
      if (seeked || this.autoScrollOnUpdate) {
        this.scrollMessageContainter();
      }
    } else if (!this.isManualChatMode) {
      this.scrollMessageByTime(currentTime);
    }
  }

  enterReadOnlyMode() {
    if (this.readOnlyMode) return;

    this.readOnlyMode = true;
    this.authEnabled = false;
    this.chatApi.socialNetworkDeauthorized();

    if (!this.showMessagesByTime) {
      // Отображаем все поступающие сообщения
      this.messageList.hideMessages = false;
      // Отображаем все сообщения, которые были подгружены ранее
      this.messageList.showAll();
      this.scrollMessageContainter();
      return;
    }
  }

  getTemplate() {
    return `
      <div class="vjs-chat-messanger-wrapper vjs-chat-custom-scrollbar">
        <div class="vjs-chat-header">
          <div class="vjs-chat-header__title">Обсуждение</div>
          <div class="vjs-chat-header__btn">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.7804 9.53027L13.844 13.4678C13.7743 13.5374 13.6917 13.5927 13.6007 13.6304C13.5097 13.6681 13.4121 13.6875 13.3137 13.6875C13.2152 13.6875 13.1176 13.6681 13.0266 13.6305C12.9356 13.5928 12.8529 13.5375 12.7833 13.4679C12.7136 13.3983 12.6584 13.3156 12.6207 13.2246C12.583 13.1336 12.5635 13.0361 12.5635 12.9376C12.5635 12.8391 12.5829 12.7416 12.6206 12.6506C12.6583 12.5596 12.7135 12.4769 12.7831 12.4072L15.4397 9.75H6.75C6.55109 9.75 6.36032 9.67098 6.21967 9.53033C6.07902 9.38968 6 9.19891 6 9C6 8.80109 6.07902 8.61032 6.21967 8.46967C6.36032 8.32902 6.55109 8.25 6.75 8.25H15.4397L12.7831 5.59277C12.6425 5.4521 12.5635 5.26132 12.5635 5.0624C12.5636 4.86348 12.6426 4.67272 12.7833 4.53209C12.924 4.39145 13.1147 4.31246 13.3137 4.31248C13.5126 4.31251 13.7033 4.39155 13.844 4.53223L17.7804 8.46973C17.921 8.61037 18 8.80112 18 9C18 9.19889 17.921 9.38963 17.7804 9.53027ZM6.75 16.5H1.5V1.5H6.75C6.94891 1.5 7.13968 1.42098 7.28033 1.28033C7.42098 1.13968 7.5 0.948912 7.5 0.75C7.5 0.551088 7.42098 0.360322 7.28033 0.21967C7.13968 0.0790178 6.94891 0 6.75 0H1.5C1.10231 0.000454817 0.721048 0.158636 0.439842 0.439842C0.158636 0.721048 0.000454817 1.10231 0 1.5V16.5C0.000454817 16.8977 0.158636 17.279 0.439842 17.5602C0.721048 17.8414 1.10231 17.9995 1.5 18H6.75C6.94891 18 7.13968 17.921 7.28033 17.7803C7.42098 17.6397 7.5 17.4489 7.5 17.25C7.5 17.0511 7.42098 16.8603 7.28033 16.7197C7.13968 16.579 6.94891 16.5 6.75 16.5Z" fill="currentColor"/>
            </svg>
          </div>
        </div>
        <div class="vjs-chat-pinned-message vjs-hidden"></div>
        <ul class="vjs-chat-content vjs-chat-custom-scrollbar vjs-chat-custom-scrollbar_hide"></ul>
        <div class="vjs-chat-form">
          <div class="vjs-chat-form-body">
            <textarea placeholder="Добавить комментарий" class="vjs-chat-form-body__textarea vjs-chat-custom-scrollbar"></textarea>
            <div class="vjs-chat-form-body__avatar-container">
              <img class="vjs-chat-form-body__avatar" src="" alt="avatar" />
            </div>
            <button class="vjs-chat-form-body__button-send">
              <svg width="20" height="22" viewBox="0 0 20 22" fill="none" class="vjs-chat-form-body__icon-send">
                <path d="M18.9478 9.69103L3.11206 0.823054C2.84561 0.673856 2.53997 0.609535 2.23599 0.638688C1.93201 0.667841 1.64416 0.789078 1.41092 0.986198C1.17769 1.18332 1.01017 1.44693 0.930757 1.7418C0.851345 2.03667 0.863823 2.34876 0.966523 2.63635L3.68564 10.2499H10.7499C10.9489 10.2499 11.1396 10.3289 11.2803 10.4695C11.4209 10.6102 11.4999 10.801 11.4999 10.9999C11.4999 11.1988 11.4209 11.3896 11.2803 11.5302C11.1396 11.6709 10.9489 11.7499 10.7499 11.7499H3.68564L0.966523 19.3634C0.886138 19.5897 0.86113 19.832 0.893593 20.07C0.926057 20.3079 1.01505 20.5347 1.15311 20.7312C1.29118 20.9277 1.47431 21.0883 1.68718 21.1995C1.90004 21.3107 2.13645 21.3693 2.37662 21.3704C2.63435 21.3699 2.88763 21.3032 3.11215 21.1767L18.9478 12.3087C19.1804 12.1784 19.3741 11.9885 19.5089 11.7584C19.6438 11.5284 19.7149 11.2665 19.7149 10.9999C19.7149 10.7333 19.6438 10.4714 19.509 10.2414C19.3742 10.0113 19.1805 9.82135 18.9478 9.69103Z" fill="currentColor"/>
              </svg>
            </button>
            <div class="vjs-chat-form-body__blocked">
              <span class="vjs-chat-form-body__blocked-text"></span>
            </div>
            <div class="vjs-chat-form-body__textarea-locker">
              <span class="vjs-chat-auth-invite"><a href="#" class="vjs-chat-menu__login">Авторизуйтесь,</a> чтобы оставить комментарий</span>
              <span class="vjs-chat-auth-disabled vjs-hidden">Обсуждение закрыто</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

Component.registerComponent('ChatMessanger', ChatMessanger);
